import { useSyncExternalStore } from 'react';
import {
  AD_CONSENT_STORAGE_KEY,
  buildAdConsent,
  parseAdConsent,
  type AdConsent,
  type AdConsentSource,
} from '@/constants/adConsent';

/**
 * This browser's "Advertising partners" choice (Your Privacy Choices). Partners
 * are on until the voter turns them off (opt-out), and Global Privacy Control
 * always turns them off.
 * Same key and JSON shape as the mobile `adConsentService`; the `vm_` prefix
 * keeps it through refresh and sign-out (see `storageUtils`).
 */

const read = (): AdConsent | null => {
  try {
    return parseAdConsent(localStorage.getItem(AD_CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
};

let snapshot: AdConsent | null = typeof window === 'undefined' ? null : read();
const listeners = new Set<() => void>();

const commit = (next: AdConsent | null) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

// False when storage is blocked or full: the choice still applies for this
// page, but would read back as the default on the next visit.
const persist = (next: AdConsent): boolean => {
  try {
    localStorage.setItem(AD_CONSENT_STORAGE_KEY, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // `key` is null when another tab clears all of storage.
    if (event.key === AD_CONSENT_STORAGE_KEY || event.key === null) commit(read());
  });
}

export const isGpcEnabled = (): boolean =>
  typeof navigator !== 'undefined' &&
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

export const adConsentStore = {
  get: (): AdConsent | null => snapshot,

  /**
   * Record a choice made in this browser (unconfirmed until the account saves
   * it). `saved` is false when it could not be written to storage.
   */
  set(granted: boolean, source: AdConsentSource): { record: AdConsent; saved: boolean } {
    const record = buildAdConsent(granted, source, snapshot);
    commit(record);
    return { record, saved: persist(record) };
  },

  /** Adopt the account's record as this browser's confirmed choice. */
  apply(record: AdConsent): void {
    commit(record);
    persist(record);
  },

  /**
   * The account saved `record`. Clears its unconfirmed flag, unless the voter
   * has changed the choice again since that save started.
   */
  markSynced(record: AdConsent): void {
    if (snapshot !== record) return;
    const synced = { ...record, pending: false };
    commit(synced);
    persist(synced);
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

// Partners wait until this browser knows the voter's account choice: a stored
// "on" here can predate an opt-out made on another device. Settled once the
// visitor is known to be a guest, or once sign-in has applied the account's
// choice; a signed-in session whose account never loads stays unsettled.
let accountSettled = false;

/** The account's choice, if any, has been applied to this browser. One-way per page. */
export const markAdConsentAccountSettled = (): void => {
  if (accountSettled) return;
  accountSettled = true;
  listeners.forEach((listener) => listener());
};

/** True unless the voter turned partners off, and never under Global Privacy Control. */
export const isAdConsentGranted = (): boolean =>
  !isGpcEnabled() && adConsentStore.get()?.granted !== false;

/**
 * Run `fire` once advertising partners are allowed: as soon as the account's
 * choice is settled (see `markAdConsentAccountSettled`) unless the voter has
 * turned them off, otherwise the first time they turn them back on this page.
 * `canFire` is checked at that moment, and a `false` skips the event rather
 * than waiting.
 */
export const whenAdConsentGranted = (
  fire: () => void,
  canFire: () => boolean = () => true
): void => {
  if (isGpcEnabled()) return;
  const ready = () => accountSettled && isAdConsentGranted();
  if (ready()) {
    if (canFire()) fire();
    return;
  }
  const unsubscribe = adConsentStore.subscribe(() => {
    if (!ready()) return;
    unsubscribe();
    if (canFire()) fire();
  });
};

export const useAdConsent = (): AdConsent | null =>
  useSyncExternalStore(adConsentStore.subscribe, adConsentStore.get, () => null);
